import { useState, useEffect, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Printer,
  ShieldCheck,
  MapPin,
  Phone,
  Calendar,
  Stethoscope,
  IdentificationCard,
  CheckCircle,
  XCircle,
  Clock,
  Buildings,
  User,
} from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import Breadcrumb from "../components/Breadcrumb";
import { softwareAppSchema, breadcrumbSchema } from "../lib/jsonLd";
import { api, getNameSlug, getPrimarySpecialty } from "../lib/api";

const VerificationReport = () => {
  const [searchParams] = useSearchParams();
  const [npiInput, setNpiInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const printRef = useRef(null);

  const generateReport = async (npiOverride) => {
    const npi = (npiOverride || npiInput).trim();
    if (!npi || !/^\d{10}$/.test(npi)) {
      setError("Please enter a valid 10-digit NPI number.");
      setReport(null);
      return;
    }

    setLoading(true);
    setError(null);
    setReport(null);

    try {
      const data = await api.getProviderEnriched(npi);
      const provider = data?.result;
      if (!provider) {
        setError("NPI number not found in the NPPES registry.");
        return;
      }

      const enriched = data.enriched || {};
      const basic = provider.basic || {};
      const isIndividual = provider.enumeration_type === "NPI-1";
      const name = isIndividual
        ? `${basic.first_name || ""} ${basic.last_name || ""}`.trim()
        : basic.organization_name || "Unknown";
      const credentials = basic.credential || "";
      const gender = basic.gender || "";
      const enumerationDate = basic.enumeration_date || "";
      const lastUpdated = basic.last_updated || "";
      const status = basic.status === "A" ? "Active" : basic.status || "Active";

      const taxonomies = (enriched.taxonomies || provider.taxonomies || []).map((t) => ({
        desc: t.plain_description || t.desc || t.code,
        code: t.code,
        primary: t.primary,
        state: t.state,
        license: t.license,
      }));

      const addresses = (provider.addresses || []).map((a) => ({
        purpose: a.address_purpose === "LOCATION" ? "Practice Location" : "Mailing Address",
        line1: a.address_1 || "",
        line2: a.address_2 || "",
        city: a.city || "",
        state: a.state || "",
        zip: a.postal_code || "",
        phone: a.telephone_number || "",
        fax: a.fax_number || "",
      }));

      const identifiers = (provider.identifiers || []).map((id) => ({
        type: id.desc || id.identifier_type || "Other",
        value: id.identifier || "",
        state: id.state || "",
      }));

      const deaFound = identifiers.some(
        (id) => id.type && id.type.toUpperCase().includes("DEA")
      );

      setReport({
        npi,
        name,
        credentials,
        gender,
        isIndividual,
        entityType: isIndividual ? "Individual (NPI-1)" : "Organization (NPI-2)",
        enumerationDate,
        lastUpdated,
        status,
        taxonomies,
        addresses,
        identifiers,
        deaFound,
        yearsRegistered: enriched.years_registered,
        experienceLevel: enriched.experience_level,
        registeredText: enriched.registered_text,
        generatedAt: new Date().toLocaleString(),
      });
    } catch {
      setError("Failed to fetch provider data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const npiParam = searchParams.get("npi");
    if (npiParam && /^\d{10}$/.test(npiParam)) {
      setNpiInput(npiParam);
      generateReport(npiParam);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      <Helmet>
        <title>Provider Verification Report | NPI Lookup</title>
        <meta
          name="description"
          content="Generate a printable provider verification report. Verify NPI, DEA status, credentials, specialty, address, and registration details for any US healthcare provider."
        />
        <link rel="canonical" href="https://npilookup.com/verify" />
        <script type="application/ld+json">{JSON.stringify(softwareAppSchema({
          name: "Provider Verification Report Generator",
          description: "Free tool to generate a printable, date-stamped verification summary for any US healthcare provider. Verifies NPI status, NPPES credentials, primary taxonomy, practice location, and registration date — useful for credentialing, audits, and onboarding.",
          url: "/verify",
        }))}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema([{ name: "Home", url: "/" }, { name: "Verification Report" }]))}</script>
      </Helmet>

      <div className="bg-white min-h-screen">
        {/* Input Section - hidden in print */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-6 print:hidden">
          <Breadcrumb items={[{ name: "Home", to: "/" }, { name: "Verification Report" }]} />
          <h1
            className="font-display font-bold text-3xl sm:text-4xl text-navy text-center mb-2"
            data-testid="verify-title"
          >
            Provider Verification Report
          </h1>
          <p className="text-center text-slate-500 mb-8 max-w-xl mx-auto">
            Generate a comprehensive, printable verification summary for any healthcare provider.
          </p>

          <div className="max-w-md mx-auto bg-slate-50 rounded-2xl border border-slate-200 p-6">
            <label htmlFor="verify-npi" className="block text-sm font-medium text-slate-700 mb-2">
              Enter NPI Number
            </label>
            <div className="flex gap-3">
              <Input
                id="verify-npi"
                type="text"
                inputMode="numeric"
                maxLength={10}
                placeholder="e.g. 1234567890"
                value={npiInput}
                onChange={(e) => setNpiInput(e.target.value.replace(/\D/g, "").slice(0, 10))}
                onKeyDown={(e) => e.key === "Enter" && generateReport()}
                className="h-12 text-base font-mono tracking-wider"
                data-testid="verify-npi-input"
                title="Enter a 10-digit NPI number to generate verification report"
              />
              <Button
                onClick={() => generateReport()}
                disabled={loading}
                className="h-12 px-6 bg-navy hover:bg-navy/90 text-white font-medium shrink-0"
                data-testid="verify-generate-btn"
              >
                {loading ? "Generating..." : "Generate"}
              </Button>
            </div>
            {error && (
              <p className="mt-3 text-sm text-red-600" data-testid="verify-error">{error}</p>
            )}
          </div>
        </section>

        {/* Report */}
        {report && (
          <section
            ref={printRef}
            className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-12"
            data-testid="verification-report"
          >
            {/* Print button */}
            <div className="flex justify-end mb-4 print:hidden">
              <Button
                onClick={handlePrint}
                variant="outline"
                className="gap-2"
                data-testid="verify-print-btn"
                title="Print or save this verification report as PDF"
              >
                <Printer className="w-4 h-4" aria-hidden="true" />
                Print / Save as PDF
              </Button>
            </div>

            {/* Report Card */}
            <div className="border-2 border-navy rounded-2xl overflow-hidden print:border print:rounded-none">
              {/* Header */}
              <div className="bg-navy px-6 py-5 print:py-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="font-display font-bold text-xl text-white print:text-lg">
                      NPI Provider Verification Report
                    </h2>
                    <p className="text-slate-300 text-sm mt-1">
                      Generated on {report.generatedAt}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${
                      report.status === "Active"
                        ? "bg-emerald-500/20 text-emerald-200"
                        : "bg-red-500/20 text-red-200"
                    }`}>
                      {report.status === "Active" ? (
                        <CheckCircle className="w-4 h-4" weight="fill" />
                      ) : (
                        <XCircle className="w-4 h-4" weight="fill" />
                      )}
                      {report.status}
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-6 print:p-4 print:space-y-4">
                {/* Provider Identity */}
                <div>
                  <h3 className="flex items-center gap-2 font-display font-semibold text-navy mb-3 text-base">
                    {report.isIndividual ? (
                      <User className="w-5 h-5" aria-hidden="true" />
                    ) : (
                      <Buildings className="w-5 h-5" aria-hidden="true" />
                    )}
                    Provider Identity
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Full Name</p>
                      <p className="font-medium text-slate-900 mt-0.5">
                        {report.name}{report.credentials ? `, ${report.credentials}` : ""}
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">NPI Number</p>
                      <p className="font-mono font-bold text-navy text-lg mt-0.5">{report.npi}</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Entity Type</p>
                      <p className="font-medium text-slate-900 mt-0.5">{report.entityType}</p>
                    </div>
                    {report.gender && (
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Gender</p>
                        <p className="font-medium text-slate-900 mt-0.5">
                          {report.gender === "M" ? "Male" : report.gender === "F" ? "Female" : report.gender}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Registration */}
                <div>
                  <h3 className="flex items-center gap-2 font-display font-semibold text-navy mb-3 text-base">
                    <Calendar className="w-5 h-5" aria-hidden="true" />
                    Registration Details
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Enumeration Date</p>
                      <p className="font-medium text-slate-900 mt-0.5">{report.enumerationDate || "N/A"}</p>
                    </div>
                    {report.yearsRegistered != null && (
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Years Registered</p>
                        <p className="font-medium text-slate-900 mt-0.5">{report.yearsRegistered} years</p>
                      </div>
                    )}
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Last Updated</p>
                      <p className="font-medium text-slate-900 mt-0.5">{report.lastUpdated || "N/A"}</p>
                    </div>
                  </div>
                </div>

                {/* Specialties */}
                {report.taxonomies.length > 0 && (
                  <div>
                    <h3 className="flex items-center gap-2 font-display font-semibold text-navy mb-3 text-base">
                      <Stethoscope className="w-5 h-5" aria-hidden="true" />
                      Specialties & Taxonomy
                    </h3>
                    <div className="space-y-2">
                      {report.taxonomies.map((t, i) => (
                        <div key={i} className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                          <div>
                            <p className="font-medium text-slate-900 text-sm">
                              {t.desc}
                              {t.primary && (
                                <span className="ml-2 text-xs bg-teal/10 text-teal px-2 py-0.5 rounded-full">Primary</span>
                              )}
                            </p>
                            <p className="text-xs text-slate-400 mt-0.5 font-mono">{t.code}</p>
                          </div>
                          {t.license && (
                            <div className="text-right">
                              <p className="text-xs text-slate-500">License: {t.license}</p>
                              {t.state && <p className="text-xs text-slate-400">{t.state}</p>}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Addresses */}
                {report.addresses.length > 0 && (
                  <div>
                    <h3 className="flex items-center gap-2 font-display font-semibold text-navy mb-3 text-base">
                      <MapPin className="w-5 h-5" aria-hidden="true" />
                      Practice Locations
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {report.addresses.map((a, i) => (
                        <div key={i} className="bg-slate-50 rounded-lg p-3">
                          <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">{a.purpose}</p>
                          <p className="text-sm text-slate-900">{a.line1}</p>
                          {a.line2 && <p className="text-sm text-slate-900">{a.line2}</p>}
                          <p className="text-sm text-slate-900">{a.city}, {a.state} {a.zip}</p>
                          {a.phone && (
                            <p className="text-sm text-slate-600 mt-1 flex items-center gap-1">
                              <Phone className="w-3 h-3" aria-hidden="true" />
                              {a.phone}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* DEA & Identifiers */}
                <div>
                  <h3 className="flex items-center gap-2 font-display font-semibold text-navy mb-3 text-base">
                    <ShieldCheck className="w-5 h-5" aria-hidden="true" />
                    DEA & Other Identifiers
                  </h3>
                  <div className={`rounded-lg p-4 border-2 ${
                    report.deaFound
                      ? "bg-emerald-50 border-emerald-300"
                      : "bg-amber-50 border-amber-300"
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      {report.deaFound ? (
                        <>
                          <CheckCircle className="w-5 h-5 text-emerald-600" weight="fill" />
                          <span className="font-semibold text-emerald-800">DEA Registration Found in NPPES</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="w-5 h-5 text-amber-600" weight="fill" />
                          <span className="font-semibold text-amber-800">No DEA Registration in NPPES</span>
                        </>
                      )}
                    </div>
                    <p className="text-xs text-slate-600">
                      {report.deaFound
                        ? "This provider has a DEA number listed in their NPPES profile."
                        : "No DEA number found in NPPES. The provider may have a DEA registration not linked to their NPI record."}
                    </p>
                  </div>
                  {report.identifiers.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {report.identifiers.map((id, i) => (
                        <div key={i} className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                          <span className="text-sm text-slate-600">{id.type}</span>
                          <span className="font-mono text-sm text-slate-900">{id.value}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Disclaimer */}
                <div className="border-t border-slate-200 pt-4">
                  <p className="text-xs text-slate-400 leading-relaxed">
                    This report is generated from the National Plan and Provider Enumeration System (NPPES)
                    maintained by the Centers for Medicare & Medicaid Services (CMS). Information is provided
                    as-is from the public NPI Registry. This report does not constitute legal verification of
                    a provider's credentials or authority to practice. Always verify information directly with
                    the appropriate licensing authority.
                  </p>
                  <p className="text-xs text-slate-400 mt-2">
                    Report generated by NPI Lookup &mdash; {report.generatedAt}
                  </p>
                </div>
              </div>
            </div>

            {/* Link back */}
            <div className="mt-6 text-center print:hidden">
              <Link
                to={`/provider/${report.npi}/${getNameSlug(report.name)}`}
                className="text-sm text-teal hover:underline font-medium"
                title={`View full profile for ${report.name}`}
              >
                View full profile for {report.name} &rarr;
              </Link>
            </div>
          </section>
        )}

        {/* Related Tools - hidden in print */}
        {!report && (
          <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 print:hidden">
            <h2 className="font-display font-semibold text-lg text-slate-900 mb-4 text-center">
              Related Tools
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Link
                to="/dea-npi-lookup"
                className="group flex items-center gap-3 bg-slate-50 rounded-xl border border-slate-200 p-5 hover:shadow-md hover:border-teal/30 transition-all"
                title="Look up DEA number using NPI"
              >
                <ShieldCheck className="w-8 h-8 text-navy" aria-hidden="true" />
                <p className="font-medium text-slate-900 group-hover:text-teal text-sm">DEA Lookup</p>
              </Link>
              <Link
                to="/specialties"
                className="group flex items-center gap-3 bg-slate-50 rounded-xl border border-slate-200 p-5 hover:shadow-md hover:border-teal/30 transition-all"
                title="Browse healthcare specialties"
              >
                <Stethoscope className="w-8 h-8 text-teal" aria-hidden="true" />
                <p className="font-medium text-slate-900 group-hover:text-teal text-sm">Specialties</p>
              </Link>
              <Link
                to="/"
                className="group flex items-center gap-3 bg-slate-50 rounded-xl border border-slate-200 p-5 hover:shadow-md hover:border-teal/30 transition-all"
                title="Search for any NPI number"
              >
                <IdentificationCard className="w-8 h-8 text-navy" aria-hidden="true" />
                <p className="font-medium text-slate-900 group-hover:text-teal text-sm">NPI Search</p>
              </Link>
            </div>
          </section>
        )}
      </div>
    </>
  );
};

export default VerificationReport;
